import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ResponsiveTable from '../../../../../shared/components/ResponsiveTable';
import {
  HeaderPropSortCallback,
  SortedColumn,
} from '../../../../../shared/components/ResponsiveTable/models';
import { User } from '../../../../../shared/models/UserModels';
import AccessCell from './TableCells/AccessCell';
import InvitedByCell from './TableCells/InvitedByCell';
import styles from './userslist.module.scss';
import AssignedProductCell from './TableCells/AssignedProductCell';
import UserCell from './TableCells/UserCell';
import { UserActions } from './models';
import { AgreementApiModel } from '../../../../../shared/models/DealModel';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../../shared/permissions';
import RowSelectionHeader, {
  RowSelectionCell,
} from './TableCells/RowSelectionHeader';
import { addUsers } from '../adminUsersLayoutSlice';

export interface UsersListProps {
  list: User[];
  isLoading: boolean;
  handleSort: HeaderPropSortCallback;
  page: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  actions: UserActions;
  accessibleDeals: AgreementApiModel[];
}

export const getColumns = (
  actions: UserActions,
  list: User[],
  sortedColumn: SortedColumn,
  isInternalAdmin: boolean = false,
): SortedColumn[] => {
  const columns: SortedColumn[] = [
    {
      name: 'rowSelection',
      title: 'Row Selection',
      cell: (cntx) => <RowSelectionCell row={cntx.row} />,
      customHeaderCell: <RowSelectionHeader users={list} />,
      width: 3,
      sortingEnabled: false,
    },
    {
      name: 'name',
      title: 'Name',
      cell: (cntx) => <UserCell actions={actions} row={cntx.row} />,
      width: 15,
      sortingEnabled: true,
      direction:
        sortedColumn?.name === 'name' ? sortedColumn.direction : undefined,
    },
    {
      name: 'countryCode',
      title: 'Country / Region',
      cell: (cntx) => {
        const country = cntx.row.original?.countryCode;
        return (
          <span className="d-block">
            {country?.toString().length > 0 ? country : 'N/A'}
          </span>
        );
      },
      width: 15,
      sortingEnabled: true,
      direction:
        sortedColumn?.name === 'countryCode'
          ? sortedColumn.direction
          : undefined,
    },
    {
      name: 'assignedProducts',
      title: 'Assigned Product(s)',
      cell: (cntx) => (
        <AssignedProductCell
          row={cntx.row}
          productsNames={cntx.row.original?.assignedProducts ?? []}
        />
      ),
      sortingEnabled: false,
    },
    {
      name: 'registration',
      title: 'Registration',
      cell: (cntx) => {
        const data = cntx.row.original;
        if (isInternalAdmin && data?.shareData === false) {
          return <span className="d-block">Opted-out</span>;
        }
        return <AccessCell row={cntx.row} />;
      },
      width: 15,
      sortingEnabled: true,
      direction:
        sortedColumn?.name === 'registration' // eslint-disable-line no-nested-ternary
          ? sortedColumn.direction
          : !sortedColumn?.name
          ? 'Desc'
          : undefined,
    },
    {
      name: 'invitedby',
      title: 'Invited By',
      cell: (cntx) => <InvitedByCell row={cntx.row} />,
      width: 18,
      sortingEnabled: true,
      direction:
        sortedColumn?.name === 'invitedby' ? sortedColumn.direction : undefined,
    },
  ];
  return columns;
};

export default function UsersList({
  list = [],
  isLoading,
  handleSort,
  onPageChange,
  page,
  totalPages,
  onPageSizeChange,
  pageSize,
  actions,
  accessibleDeals,
}: UsersListProps) {
  // Permissions hook to check access rights
  const { hasAccess, isLoading: isAccessLoading } = usePermissions();
  // State for the currently sorted column
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>();
  const dispatch = useDispatch();
  // Memoized columns definition based on dependencies
  const columns = useMemo(
    () =>
      getColumns(
        actions,
        list,
        sortedColumn,
        hasAccess({ permissions: [PERMISSIONS.ManageElevatedAdmins] }),
      ),
    [isLoading, accessibleDeals, isAccessLoading, sortedColumn, list],
  );
  // Effect to reset selected users when the list changes
  useEffect(() => {
    if (list.length > 0) {
      dispatch(addUsers([]));
    }
  }, []);

  return (
    <div className={styles.usersTableContainer} data-testid="user-list">
      {!isLoading && (
        <ResponsiveTable
          data={list}
          paginationProps={{
            page,
            totalPages: list?.length > 0 ? totalPages : 0,
            onPageChange,
            onPageSizeChange,
            pageSize,
            showPageSizes: list?.length > 0,
            customClassName: styles.tableUsersPagination,
            globalPageSize: true,
          }}
          onSort={(col) => {
            setSortedColumn(col);
            handleSort(col);
          }}
          columns={columns}
          showPagination={list?.length > 0}
          className={`overflow-auto user-list-data ${styles.tableUsers}`}
        />
      )}
      {isLoading && <p className="py-2">Loading...</p>}
    </div>
  );
}
